// Copyright (c) Frisbii GmbH. All rights reserved.
'use strict';

var DashboardController = function ($scope, $rootScope, $filter, PlanGroup, PriceLists, PlanGroupStats, PSPWarning,
    PaymentTransaction, Email, gettextCatalog, gettext, Order, NotificationsGroups, DatepickerUtils) {
    $scope.maxToDate = moment().toDate();
    $scope.tooltipsArray = Object.freeze({
        "MRR": gettext("Monthly Recurring Revenue - a measure of your predictable revenue stream normalised across different subscription terms."),
        "Billed Turnover": gettext("Shows the total revenue within the selected period of time."),
        "Active Contracts": gettext("Number of active contracts"),
        "Chargeable Contracts": gettext("Displays the number of all active and chargeable contracts"),
        "Contract": gettext('New: the number of contracts started in the selected period of time<br/>Ended: the count of contracts terminated within the selected period of time'),
        "New": gettext("the number of contracts started in the selected period of time"),
        "Ended": gettext('the count of contracts terminated within the selected period of time')
    });

    if ($scope.adminOrInRole(['Operations', 'ReadOnlyOperations'])) {
        $scope.orders = [];
        $scope.transactions = [];
        $scope.emails = [];

        $scope.loadEmails = function () {
            $rootScope.loadData('emails', function (promise) {
                return Email.query({ status: 'Undelivered', skipNew: true },
                    function (value) {
                        $scope.emails = value;
                        promise.resolve(value);
                    }, function (value) {
                        console.log("Email get error");
                        promise.reject(value);
                    });
            });
        };

        $scope.getCustomerName = function (order) {
            if (order.Contract)
                return order.Contract.CustomerName;
            if (order.Customer) {
                if (order.Customer.CompanyName)
                    return order.Customer.CompanyName;
                if (order.Customer.FirstName) {
                    if (order.Customer.LastName)
                        return order.Customer.LastName + ', ' + order.Customer.FirstName;
                    return order.Customer.FirstName;
                }
                if (order.Customer.LastName)
                    return order.Customer.LastName;
            }
            return "";
        };

        $scope.loadOrders = function() {
            $rootScope.loadData('orders',
                function(promise) {
                    return Order.query({
                        take: 10,
                        status: 'Incomplete'
                    },
                        function(value) {
                            $scope.orders = value;
                            promise.resolve(value);
                        },
                        function(value) {
                            console.log("Orders get error");
                            promise.reject(value);
                        });
                });
        };

        $scope.loadNotificationsGroups = function () {
            $rootScope.loadData('notificationGroups', function (promise) {
                return NotificationsGroups.query({}, function (value) {
                    $scope.notificationGroups = value;
                    promise.resolve(value);
                }, function (value) {
                    console.log("NotificationsGroups get error");
                    promise.reject(value);
                });
            });
        }

        $scope.loadOrders();
        $scope.loadEmails();
        $scope.loadNotificationsGroups();

        $scope.statusClass = function (status) {
            switch (status) {
                case 'Succeeded':
                case 'PreliminarySucceeded':
                    return "label-success";
                case 'Failed':
                case 'Chargeback':
                    return "label-important";
                default:
                    return '';
            }
        };

        $scope.pspWarnings = PSPWarning.query();
    }

    if ($scope.adminOrInRole(['Reports'])) {
        var chartStats = null;
        var skipDateWatcher = false;
        var skipDateToWatcher = false;
        // get selected range
        var __currentPeriod = {};
        var __loadedPeriod = {};
        $scope.redrawChartFlag = true; // just toggle it to redraw chart

        // After switching the language we should redraw current chart to update language specific stuff, like date names.
        $scope.$watch(function () {
            return gettextCatalog.currentLanguage;
        }, function () {
            $scope.redrawChartFlag = !$scope.redrawChartFlag;
        });

        var PERIODS = $scope.PERIODS = {
            DateRange: 'Date Ranges',
            SinceYesterday: 'Since yesterday',
            Last7days: 'Last 7 days',
            Last30days: 'Last 30 days',
            Last90days: 'Last 90 days'
        };

        const DATE_RANGE_PERIODS = $scope.DATE_RANGE_PERIODS = {
            RangeDays: 'Range Days',
            RangeWeeks: 'Range Weeks',
            RangeMonths: 'Range Months',
            RangeQuarters: 'Range Quarters',
            RangeYears: 'Range Years'
        };

        var defaultDateFormats = {
            'dateformat24h': 'h a',
            'dateformat7d': 'EEE h a',
            'dateformat30d': 'MMM d',
            'dateformat90d': 'MMM d',
            'dateformat1y': 'MMM y'
        };

        //should be added to i18n pack
        var defaultDateFormatsDE = {
            'dateformat24h': 'HH:mm',
            'dateformat7d': 'EEE HH:mm',
            'dateformat30d': 'MMM d',
            'dateformat90d': 'MMM d',
            'dateformat1y': 'MMM y'
        };

        var getDateFormat = function(key) {
            return (gettextCatalog.currentLanguage === 'de') ? defaultDateFormatsDE[key] : defaultDateFormats[key];
        };

        // predefined periods
        $scope.periods = {
            'Since yesterday': {
                numberOfSteps: 24,
                stepSize: 1,
                xAxisFormat: getDateFormat('dateformat24h'),
                xStep: 2, // Label every 2nd hour
                days: 1,
                dayBased: false
            },
            'Last 7 days': {
                numberOfSteps: 7 * 24,
                stepSize: 1,
                xAxisFormat: getDateFormat('dateformat7d'),
                xStep: 24, // Label every day
                days: 7,
                dayBased: false
            },
            'Last 30 days': {
                numberOfSteps: 29,
                stepSize: 24, // 1 day step
                xAxisFormat: getDateFormat('dateformat30d'),
                xStep: 2, // Label every 2nd day
                days: 30,
                dayBased: false
            },
            'Last 90 days': {
                numberOfSteps: 13,
                stepSize: 24*7, // 1 day step
                xAxisFormat: getDateFormat('dateformat90d'),
                xStep: 1, // Label every 7th day
                days: 90,
                dayBased: false
            },
            'Date Ranges': [
                {
                    name: DATE_RANGE_PERIODS.RangeDays,
                    minDays: 1,
                    maxDays: 7,
                    numberOfSteps: 7 * 24,
                    stepSize: 1,
                    xAxisFormat: getDateFormat('dateformat7d'),
                    xStep: 12, // 2 labels per day
                    dayBased: false
                },
                {
                    name: DATE_RANGE_PERIODS.RangeWeeks,
                    minDays: 7,
                    maxDays: 30,
                    numberOfSteps: 30,
                    stepSize: 24,
                    xAxisFormat: getDateFormat('dateformat30d'),
                    xStep: 2, // Label every 2nd day
                    dayBased: false
                },
                {
                    name: DATE_RANGE_PERIODS.RangeMonths,
                    minDays: 30,
                    maxDays: 90,
                    numberOfSteps: 13,
                    stepSize: 24 * 7, // 7 days step
                    xAxisFormat: getDateFormat('dateformat90d'),
                    xStep: 1, // One label per week
                    dayBased: true
                },
                {
                    name: DATE_RANGE_PERIODS.RangeQuarters,
                    minDays: 90,
                    maxDays: 366,
                    numberOfSteps: 12,
                    stepSize: 24 * 4 * 7,
                    xAxisFormat: getDateFormat('dateformat1y'),
                    xStep: 1, // 1 label per 28 days
                    dayBased: true
                },
                {
                    name: DATE_RANGE_PERIODS.RangeYears,
                    minDays: 366,
                    maxDays: 5 * 366,
                    numberOfSteps: 1,
                    stepSize: 24 * 30 * 3, // 90 days step
                    xAxisFormat: getDateFormat('dateformat1y'),
                    xStep: 1, // One label per 90 days
                    dayBased: true
                }
            ]
        };

        var yAxisTickFormatFunction = function () {
            return function (d) {
                return d3.format('d')(d);
            };
        };

        var yAxisTickFormatFunction2 = function () {
            return function (d) {
                return Math.abs(d);
            };
        };

        //$scope.loadOrders();
        var xAxisTickFormatFunction = function () {
            return function (d) {
                // Process UTC based hours
                var date = new Date(d * 60 * 60 * 1000);
                return $filter('date')(date, __currentPeriod.xAxisFormat);
            };
        };

        $scope.subscribersTooltipContent = function() {
            return function(key, y, x) {
                return '<strong>' +
                    key +
                    '</strong>' +
                    '<p><strong>' +
                    x +
                    '</strong> on <strong>' +
                    y +
                    '</strong></p>';
            };
        };

        $scope.xAxisTickValuesFunction = function () {
            return function (d) {
                var values = d[0].values;
                var step = __currentPeriod.xStep;
                var result = [];
                for (var i = 0; i < values.length; i += step) {
                    result.push(values[i][0]);
                }
                return result;
            };
        };

        var getSelectedKPI = function () {
            var selectedKPI = $scope.kpis[0];
            var kpiLabel = localStorage.getItem('kpi');
            if (kpiLabel) {
                var kpis = $scope.kpis.filter(function (kpi) {
                    return kpi.sum.label === kpiLabel;
                });
                if (kpis.length > 0) {
                    selectedKPI = kpis[0];
                }
            }

            return selectedKPI;
        };

        $scope.kpis = [
            {
                absoluteValueFilter: function (v, currency) {
                    return $filter('number')(v, 0) + ' ' + currency;
                },
                chart: {
                    data: [],
                    xAxisTickFormatFunction: xAxisTickFormatFunction,
                    yAxisTickFormatFunction: yAxisTickFormatFunction
                },
                sum: { label: 'MRR' }
            },
            {
                //according IT-2389
                absoluteValueFilter: function (v, currency, startValue) {
                    return $filter('number')(v - startValue, 0) + ' ' + currency;
                },
                chart: {
                    data: [],
                    xAxisTickFormatFunction: xAxisTickFormatFunction,
                    yAxisTickFormatFunction: yAxisTickFormatFunction
                },
                sum: { label: 'Billed Turnover' }
            },
            {
                absoluteValueFilter: function (v) {
                    return $filter('number')(v, 0);
                },
                chart: {
                    data: [],
                    xAxisTickFormatFunction: xAxisTickFormatFunction,
                    yAxisTickFormatFunction: yAxisTickFormatFunction
                },
                sum: { label: 'Active Contracts' }
            },
            {
                absoluteValueFilter: function (v) {
                    return $filter('number')(v, 0);
                },
                chart: {
                    data: [],
                    xAxisTickFormatFunction: xAxisTickFormatFunction,
                    yAxisTickFormatFunction: yAxisTickFormatFunction
                },
                sum: { label: 'Chargeable Contracts' }
            },
            {
                absoluteValueFilter: function (v) {
                    return $filter('number')(v, 0);
                },
                chart: {
                    data: [],
                    xAxisTickFormatFunction: xAxisTickFormatFunction,
                    yAxisTickFormatFunction: yAxisTickFormatFunction2
                },
                sum: {label: 'Contracts'}
            }
        ];

        var setSubscribersChartForceYFromLocalStorage = function () {
            //it's not possible to save this value into kpi.chart because angularjs-nvd3-directives.js has unexpected behaviour
            //after first initialization it do not reload setting only new data
            var max = localStorage.getItem("subscribersAbsoluteMax");
            $scope.subscribersChartForceY = [-max, max];
        };

        // get period
        var getPeriod = function () {
            return $scope.periods[$scope.selectedPeriodName];
        };

        var updateDateRange = function () {
            if ($scope.selectedPeriodName !== PERIODS.DateRange) {
                let period = getPeriod();
                skipDateToWatcher = skipDateWatcher = true;
                let nowMoment = moment();

                if (period.dayBased) {
                    let dateFromMoment = moment(nowMoment).subtract(period.days, 'days').add(1, 'days');
                    let dateFrom = DatepickerUtils.toBerlinMidnightTime(dateFromMoment.toDate());
                    $scope.dateRange.dateFrom = dateFrom;

                    let dateTo = DatepickerUtils.toBerlinMidnightTime(moment(nowMoment).toDate());
                    $scope.dateRange.dateTo = dateTo;
                } else {
                    let dateFrom = moment(nowMoment).subtract(period.days, 'days').toDate();
                    dateFrom = DatepickerUtils.toBerlinMidnightTime(dateFrom);
                    $scope.dateRange.dateFrom = dateFrom;

                    setTodayAsDateTo(nowMoment);
                }
            }

            if ($scope.dateRange.dateTo) DatepickerUtils.markAsAlreadyInBrowserTimezone($scope.dateRange.dateTo);
            if ($scope.dateRange.dateFrom) DatepickerUtils.markAsAlreadyInBrowserTimezone($scope.dateRange.dateFrom);
        };
        
        let setTodayAsDateTo = function(nowMoment) {
            let dateTo = moment(nowMoment).subtract(1, 'hours').toDate();
            $scope.dateRange.dateTo = dateTo;
        }

        let getDateRange = function () {
            let start = localStorage.getItem('DateRangeStart');
            let end = localStorage.getItem('DateRangeEnd');
            return {
                dateFrom: start === null || start === "null" ? null : new Date(start),
                dateTo: end === null || end === "null"  ? null : new Date(end)
            };
        };

        let initDateRange = function () {
            $scope.dateRange = getDateRange();
        };

        var initFromLocalStorage = function (periods, planGroupsArray) {
            let selectedPeriodName = PERIODS.Last30days;
            let periodName = localStorage.getItem('kpi-period-name');
            if (periodName && periodName in periods)
                selectedPeriodName = periodName;

            let selectedPlanGroup = planGroupsArray[0];
            let planGroupId = localStorage.getItem('kpi-plan-group-id');
            if (planGroupId) {
                let planGroups = planGroupsArray.filter(function (p) {
                    return p.Id === planGroupId;
                });
                if (planGroups.length > 0)
                    selectedPlanGroup = planGroups[0];
            }

            let selectedKPI = getSelectedKPI();
            setSubscribersChartForceYFromLocalStorage();
            $scope.selectedPeriodName = selectedPeriodName;
            $scope.selectedPlanGroup = selectedPlanGroup;
            $scope.selectedKPI = selectedKPI;
            $scope.selectedKpiTab = $scope.selectedKPI.sum.label;

            if ($scope.selectedPeriodName === PERIODS.DateRange)
                initDateRange();
            else {
                skipDateWatcher = true;
                updateDateRange();
            }
        };

        $scope.selectKPI = function (kpi) {
            localStorage.setItem('kpi', kpi.sum.label);
            $scope.selectedKPI = kpi;
            $scope.selectedKpiTab = $scope.selectedKPI.sum.label;
        };

        // todo: find out how to deal with Localized Strings
        // query all plan group of the user
        $scope.planGroups = PlanGroup.query({}, function () {
            if ($scope.planGroups.length > 0) {
                initFromLocalStorage($scope.periods, $scope.planGroups);
                $scope.loadPriceLists($scope.loadPlanGroupStats);
            }
        });

        $scope.loadPriceLists = function (callback) {
            PriceLists.query({planGroupId: $scope.selectedPlanGroup.Id}, function (priceLists) {
                $scope.selectedCurrency = priceLists.find(priceList => priceList.Id === $scope.selectedPlanGroup.DefaultPriceListId).Currency;
                $scope.currencies = Array.from(new Set(priceLists.map(x => x.Currency)));
                callback();
            });
        }

        $scope.dateRange = { dateFrom: null, dateTo: null };

        let skipStartDateWatcher = false;
        let skipEndDateWatcher = false;
        $scope.$watch("dateRange.dateFrom", function () {            
            if (!skipDateWatcher && !skipStartDateWatcher && $scope.dateRange.dateFrom) {
                let now = moment();
                let lastHourMoment = moment(now).subtract(1, 'hours');
                let lastHour = lastHourMoment.toDate();
                let oneDayBeforeMoment = moment(lastHourMoment).subtract(1, 'days');
                let oneDayBefore = oneDayBeforeMoment.toDate();

                // Make sure start date is not higher than last hour - 1 day
                if ($scope.dateRange.dateFrom > oneDayBefore)
                    $scope.dateRange.dateFrom = oneDayBefore;

                skipStartDateWatcher = true;

                // Make sure end date is set and higher than start date
                if (!$scope.dateRange.dateTo || $scope.dateRange.dateFrom > $scope.dateRange.dateTo) {
                    $scope.dateRange.dateTo = lastHour;
                    skipEndDateWatcher = true;
                }

                $scope.dateRange.dateFrom = DatepickerUtils.toBerlinMidnightTime($scope.dateRange.dateFrom);

                // A date was set manually so set manual date range mode
                $scope.selectedPeriodName = PERIODS.DateRange;
            } else {
                skipDateWatcher = false;
                skipStartDateWatcher = false;
            }
        });

        $scope.$watch("dateRange.dateTo", function () {            
            if (!skipEndDateWatcher && !skipDateToWatcher)
                $scope.selectedPeriodName = PERIODS.DateRange;

            if (!skipEndDateWatcher && $scope.dateRange.dateTo) {
                let nowMoment = moment();
                let lastHour = moment(nowMoment).subtract(1, 'hours').toDate();
                let todayBerlinMidnight = DatepickerUtils.toBerlinMidnightTime(nowMoment.toDate());

                // If end date is today set to the last hour
                if ($scope.dateRange.dateTo >= todayBerlinMidnight)
                    $scope.dateRange.dateTo = lastHour;
                // Otherwise make sure it is midnight
                // Setting midnight excludes the selected day, but we want to make sure the UI shows the correct day.
                // When requesting the actual data the period is prolonged by one day so it is included
                else
                    $scope.dateRange.dateTo = DatepickerUtils.toBerlinMidnightTime($scope.dateRange.dateTo);
                
                skipEndDateWatcher = true;
            }
            else
                skipEndDateWatcher = false;

            skipDateToWatcher = false;
            
            if ($scope.dateRange.dateTo)
                DatepickerUtils.markAsAlreadyInBrowserTimezone($scope.dateRange.dateTo);
        });

        let setDateRange = function () {
            localStorage.setItem('DateRangeStart', $scope.dateRange.dateFrom ? $scope.dateRange.dateFrom : null);
            localStorage.setItem('DateRangeEnd', $scope.dateRange.dateTo ? $scope.dateRange.dateTo : null);
        };

        // get period by date range
        let getPeriodByRange = function () {
            // check start date
            if (!$scope.dateRange.dateFrom) {
                $scope.notify("error", gettextCatalog.getString(gettext('The selected time period is not valid. Choose a start date please.')));
                return null;
            }
            
            if(!$scope.dateRange.dateTo) {
                let nowMoment = moment();
                setTodayAsDateTo(nowMoment);
            }
            
            setDateRange();

            // check end date
            let diffDays = moment($scope.dateRange.dateTo).diff(moment($scope.dateRange.dateFrom), 'days');

            function getSubPeriod(periods) {
                for (let i = 0; i < periods.length; i++) {
                    let period = periods[i];
                    if (diffDays >= period.minDays && diffDays <= period.maxDays)
                        return period;
                }
                
                return null;
            }

            if (diffDays === 0)
                diffDays = 1;

            if (diffDays < 0) {
                $scope.notify("error", gettextCatalog.getString(gettext('The selected time period is not valid. The end date can\'t be less than the start date.')));
                return null;
            }

            let period = getSubPeriod(getPeriod());
            if (period === null) {
                $scope.notify("error", gettextCatalog.getString(gettext('The selected time period is too long.')));
                return null;
            }

            // calculate count of steps according to the selected period
            let _period = {};
            angular.copy(period, _period);
            _period.numberOfSteps = Math.ceil(diffDays * 24 / _period.stepSize);

            return _period;
        };

        var getSelectedPeriod = function () {
            __currentPeriod = PERIODS.DateRange === $scope.selectedPeriodName ? getPeriodByRange() : getPeriod();
            return __currentPeriod;
        };

        $scope.selectPeriod = function (name) {
            $scope.selectedPeriodName = name;
            updateDateRange();
            localStorage.setItem('kpi-period-name', name);
            $scope.loadPlanGroupStats();
        };

        $scope.selectedPlanGroupChanged = function () {
            localStorage.setItem('kpi-plan-group-id', $scope.selectedPlanGroup.Id);
            $scope.loadPriceLists($scope.loadPlanGroupStats);
        };
        $scope.selectedCurrencyChanged = function () {
            $scope.loadPlanGroupStats();
        };
        let checkIfNeedGroupByMonths = function (selectedPeriod) {
            if (selectedPeriod && selectedPeriod.name && selectedPeriod.name === DATE_RANGE_PERIODS.RangeQuarters)
                return true;

            return false;
        };

        let getFilterData = function (selectedPeriod) {
            let todayCetMidnight = DatepickerUtils.toBerlinMidnightTime(moment().toDate());
            let correctedTo = new Date($scope.dateRange.dateTo);
            if (correctedTo < todayCetMidnight) // include the whole day to search, if we're not in "today"
                correctedTo = DatepickerUtils.toBerlinMidnightTime(moment(correctedTo).add(1, 'days').toDate());

            let data = {
                planGroupId: $scope.selectedPlanGroup.Id,
                numberOfSteps: selectedPeriod.numberOfSteps + 1,
                stepSize: selectedPeriod.stepSize,
                groupByMonths: checkIfNeedGroupByMonths(selectedPeriod),
                currency: $scope.selectedCurrency,
                dateFrom: $scope.dateRange.dateFrom.toISOString(),
                dateTo: correctedTo.toISOString(),
            };

            return data;
        };

        // round values because float numbers are not displayed in the legend box
        var roundValues = function (items) {
            for (var j = 0; j < items.length; j++) {
                var values = items[j].Item2;
                for (var i = 0; i < values.length; i++) {
                    var n = values[i];
                    values[i] = isNaN(n) ? 0 : Math.round(n);
                }
            }

            return items;
        };

        // sort values
        var sortItems = function (items) {
            var result = items.sort(function (a, b) {
                var maxValue1 = a.Item2.slice(0).sort().reverse()[0];
                var maxValue2 = b.Item2.slice(0).sort().reverse()[0];
                return (maxValue1 < maxValue2) ? 1 : (maxValue1 > maxValue2 ? -1 : 0);
            });
            return result;
        };

        // update values regarding requirements in the IT-1205
        var updateValues = function (items) {
            return sortItems(roundValues(items));
        };

        // agregate data for list plans
        var aggregateData = function (items, start, end) {
            var result = [];
            for (var i = start; i < end; i++) {
                var item = items[i];
                if (item) {
                    var data = item.Item2;
                    for (var j = 0; j < data.length; j++) {
                        var v = data[j];
                        if (!isNaN(v)) {
                            result[j] = isNaN(result[j]) ? v : result[j] + v;
                        }
                    }
                }
            }

            return result;
        };

        // update plan name, if length > default length then cut off the name to default length
        var updatePlanName = function (name, filter) {
            if (filter.maxNameLength && name.length > filter.maxNameLength) {
                return name.substring(0, filter.maxNameLength) + "...";
            } else {
                return name.substring(0, 140); // max length of name
            }
        };

        // create and return list of plans according to the filter
        var updateData = function (items, filter) {
            var previousData = [];
            var otherData = [];
            var chartsData = [];

            var countPlans = items.length;

            var start = Math.max(0, filter.start);
            var end = filter.end < 1 ? countPlans : Math.min(countPlans, filter.end);

            if (start > 0) {
                previousData.push({
                    'Item1': gettextCatalog.getString(gettext('Previous')),
                    'Item2': aggregateData(items, 0, start)
                });
            }

            for (var i = start; i < end; i++) {
                var item = items[i];
                var name = updatePlanName($filter('babel')(item.Item1), filter);
                chartsData.push({ 'Item1': name, 'Item2': item.Item2.slice(0) });
            }

            if (end < countPlans) {
                otherData.push({
                    'Item1': gettextCatalog.getString(gettext('Other')),
                    'Item2': aggregateData(items, end, countPlans)
                });
            }

            var result = $scope.chartConfig.otherAtFirst
                ? otherData.concat(chartsData, previousData)
                : previousData.concat(chartsData, otherData);

            return result;
        };

        // filtering data according to the selected values
        var filterData = function (stats, filter) {
            var result = {};
            result['MonthlyRecurringRevenueStats'] =
                updateData(updateValues(stats.MonthlyRecurringRevenueStats), filter);
            result['BilledTurnOverStats'] = updateData(updateValues(stats.BilledTurnOverStats), filter);
            result['TotalNumberOfContractsStats'] = updateData(updateValues(stats.TotalNumberOfContractsStats), filter);
            result['PayingContractsStats'] = updateData(updateValues(stats.PayingContractsStats), filter);

            return result;
        };

        var getSubscriptions = function(chartData) {
            var result = [];
            for (var i = 0; i < chartData.SubscriptionsStats.length; i++) {
                var unsubscriptions = angular.copy(chartData.UnsubscriptionsStats[i]);
                var data = unsubscriptions.Item2;

                for (var j = 0; j < data.length; j++) {
                    data[j] = -1 * data[j];
                }
                unsubscriptions.Item1 = gettextCatalog.getString(gettext('Ended Contracts'));
                unsubscriptions.color = "#e36d6d";
                unsubscriptions.max = Math.min.apply(Math, data);
                result.push(unsubscriptions);

                var subscriptions = angular.copy(chartData.SubscriptionsStats[i]);
                subscriptions.Item1 = gettextCatalog.getString(gettext('New Contracts'));
                subscriptions.color = "#58ae53";
                subscriptions.max = Math.max.apply(Math, subscriptions.Item2);
                result.push(subscriptions);
            }

            return result;
        };

        // create filter according to the view mode and selected values in comboxes
        var getFilter = function () {
            var filter = { start: 0, end: $scope.displayPlans, maxNameLength: 20 };
            if ($scope.chartConfig.useRange) {
                filter.start = Math.max($scope.startRange - 1, 0);
                filter.end = Math.min($scope.endRange, $scope.countPlans);
            }

            return filter;
        };

        // push the updated data to charts
        var updateChart = function () {
            if (chartStats) {
                var chartData = chartStats.PlanGroupKPI;
                var agregatedChartData = filterData(chartData, getFilter());

                if (chartData.NoData) {
                    for (var k = 0; k < $scope.kpis.length; k++) {
                        $scope.kpis[k].chart.data = [];
                        $scope.kpis[k].sum.absolute = 0;
                        $scope.kpis[k].sum.delta = NaN;
                        $scope.kpis[k].noData = true;
                    }
                    $scope.kpisSubscribtions = { chart: { data: [] }, sum: 0, noData: true };
                    $scope.kpisUnsubscribtions = { chart: { data: [] }, sum: 0, noData: true };

                    return;
                }

                $scope.$fillChartData($scope.kpis[0],
                    chartData.XAxisValues,
                    agregatedChartData.MonthlyRecurringRevenueStats,
                    $scope.selectedCurrency);

                $scope.$fillChartData($scope.kpis[1],
                    chartData.XAxisValues,
                    agregatedChartData.BilledTurnOverStats,
                    $scope.selectedCurrency);

                $scope.$fillChartData($scope.kpis[2],
                    chartData.XAxisValues,
                    agregatedChartData.TotalNumberOfContractsStats,
                    $scope.selectedCurrency);

                $scope.$fillChartData($scope.kpis[3],
                    chartData.XAxisValues,
                    agregatedChartData.PayingContractsStats,
                    $scope.selectedCurrency);

                $scope.$fillChartData($scope.kpis[4],
                    chartData.XAxisValues,
                    getSubscriptions(chartData),
                    $scope.selectedCurrency);

                // calculate data for subscriptions
                $scope.updateSubscribtionsChartData(chartData.XAxisValues,
                    chartData.SubscriptionsStats,
                    chartData.UnsubscriptionsStats);
                //$scope.$fillChartData($scope.kpis[5], chartData.XAxisValues, chartData.UnsubscriptionsStats, chartStats.Currency);
            }
        };

        //call form ui, update filter's values
        $scope.updateFilter = function () {
            if ($scope.chartConfig.useRange && $scope.startRange >= $scope.endRange) {
                $scope.endRange = Math.min($scope.startRange + $scope.chartConfig.step, $scope.countPlans);
            }

            updateChart();
        };

        // update values for selection coboboxes
        var updateToolbox = function () {

            $scope.rangeList = [];
            $scope.rangePlans = [];
            $scope.countPlans = 0;

            if (chartStats) {

                var selectedKpi = getSelectedKPI();
                if (!selectedKpi || selectedKpi.chart.data === null) {
                    return;
                }

                var countPlans = $scope.countPlans = selectedKpi.chart.data.length;
                var step = $scope.displayPlans = $scope.chartConfig.step;

                if (countPlans > step) {

                    for (var i = 0; i < countPlans; i += step) {
                        var v = (i + step);
                        $scope.rangeList.push({ value: v, label: v });
                    }
                    for (var j = 0; j < countPlans; j++) {
                        $scope.rangePlans.push(j + 1);
                    }

                    $scope.rangeList.push({ value: -1, label: gettextCatalog.getString(gettext('All')) });
                }
            }
        };

        $scope.$watch("selectedKPI", function () {
            updateToolbox();
        });

        $scope.$fillChartData = function (kpi, xAxis, data, currency) {
            kpi.noData = false;
            var values = [];
            var lastAbsolute = 0;
            var currentAbsolute = 0;
            var maxValuesArray = [];

            for (var j = 0; j < data.length; j++) {
                var item = { key: data[j].Item1, values: [], color: data[j].color };
                maxValuesArray.push(data[j].max);
                for (var i = 0; i < data[j].Item2.length; i++) {
                    var value = data[j].Item2[i];
                    //For a day based view we need to subtract 1 day to show the correct x values: e.g. 12.9.18 0:00 --> data for 11.9.18
                    item.values.push([xAxis[i] , value]);
                }
                values.push(item);
                lastAbsolute += data[j].Item2[0];
                currentAbsolute += data[j].Item2[data[j].Item2.length - 1];
            }

            if (kpi.sum.label === "Contracts") {
                var actualMin = Math.min.apply(null, maxValuesArray);
                var actualMax = Math.max.apply(null, maxValuesArray);
                var absoluteMax = Math.max.apply(null, [Math.abs(actualMin), actualMax]);
                if (absoluteMax !== 0) {
                    var additionValue = absoluteMax / 10;
                    absoluteMax = absoluteMax + Math.floor(additionValue);
                }
                //this value should be stored in local storage to get it from them when we do init from local storage (why we save info in local storage?)
                localStorage.setItem('subscribersAbsoluteMax', absoluteMax);
                setSubscribersChartForceYFromLocalStorage();
            }

            if (kpi.sum.label === 'Billed Turnover')
                //Remove for billed turnover. The formular is wrong in that case
                kpi.sum.delta = undefined;
            else
                kpi.sum.delta = lastAbsolute !== 0 ? (currentAbsolute / lastAbsolute - 1) * 100 : NaN;
            //according IT-2389
            kpi.sum.absolute = kpi.absoluteValueFilter(currentAbsolute, currency, lastAbsolute);
            kpi.chart.data = values;
        };

        $scope.updateSubscribtionsChartData = function(xAxis, subscriptionData, unsubscriptionData) {
            $scope.kpisSubscribtions = { chart: { data: [] }, sum: 0, noData: false };
            $scope.kpisUnsubscribtions = { chart: { data: [] }, sum: 0, noData: false };

            for (var i = 0; i < subscriptionData.length; i++) {
                for (var j = 0; j < subscriptionData[i].Item2.length; j++) {
                    $scope.kpisSubscribtions.sum += subscriptionData[i].Item2[j];
                    $scope.kpisUnsubscribtions.sum += unsubscriptionData[i].Item2[j];
                }
            }
        };

        // todo: find out why it's getting called twice on start-up.

        $scope.chartConfig = {
            otherAtFirst: true,
            step: 5,
            useRange: false
        };
        $scope.listPlans = [];
        $scope.rangePlans = [];
        $scope.countPlans = 0;
        $scope.displayPlans = $scope.chartConfig.step;
        $scope.startRange = 1;
        $scope.endRange = $scope.chartConfig.step;

        $scope.displayToolbar = function () {
            return $scope.countPlans > $scope.chartConfig.step;
        };

        // load plans statistics
        $scope.loadPlanGroupStats = function () {
            let selectedPeriod = getSelectedPeriod();
            if (selectedPeriod) {
                __loadedPeriod = selectedPeriod;
                let data = getFilterData(selectedPeriod);

                $rootScope.loadData('kpi', function (promise) {
                    return PlanGroupStats.query(data, function (value) {
                        promise.resolve(value);
                        chartStats = value;
                        updateChart();
                        updateToolbox();
                    }, function (value) {
                        promise.reject(value);
                    });
                });
            }
        };
    }

    // ReSharper disable JsUnreachableCode
    gettext('dateformat24h');
    gettext('dateformat7d');
    gettext('dateformat30d');
    gettext('dateformat90d');
    gettext('dateformat1y');
};
DashboardController.$inject = ['$scope', '$rootScope', '$filter', 'PlanGroup', 'PriceLists', 'PlanGroupStats',
    'PSPWarning', 'PaymentTransaction', 'Email', 'gettextCatalog', 'gettext', 'Order', 'NotificationsGroups',
    'DatepickerUtils'];
